// Pure reporting over recorded activity. Recurring arrangements and projected
// paydays are not transactions, and moving money is not spending it.
import { availableCash, isCreditAccount } from "../../../../shared/financeCash.ts";

export type FinanceTransactionKind = "income" | "expense" | "transfer" | "debt-payment";

export interface FinanceSourceRow {
  id?: string;
  date?: string;
  iso?: string;
  date_iso?: string;
  dateIso?: string;
  year?: number;
  month?: string;
  amount?: number | string;
  recurs?: unknown;
  recurring?: unknown;
  source?: string;
  note?: string;
  name?: string;
  cat?: string;
  category?: string;
  account?: string;
  from?: string;
  to?: string;
  debtId?: string;
}

export interface FinanceAnalyticsInput {
  accounts?: readonly { id: string; name: string; type: string; balance: number }[];
  incomes?: readonly FinanceSourceRow[];
  expenses?: readonly FinanceSourceRow[];
  transfers?: readonly FinanceSourceRow[];
  // The API's reconciled paymentHistory, exposed as st.payments. debtPayments
  // is another view of the same events and must never be added to this history.
  payments?: readonly FinanceSourceRow[];
  debtPayments?: readonly unknown[];
  debts?: readonly { id: string; name: string; kind?: string; balance: number; facility?: string; lender?: string; direction?: string; receivable?: boolean }[];
}

export interface FinanceAnalyticsOptions {
  month?: "all" | string;
  account?: string | null;
  category?: string | null;
  nowIso?: string;
  fallbackYear?: number;
  ownerName?: string;
}

export interface FinanceCategory { key: string; label: string }
export interface FinanceTransaction {
  id: string;
  kind: FinanceTransactionKind;
  iso: string;
  date: string;
  label: string;
  category: string;
  categoryLabel: string;
  account: string;
  accountName: string;
  amount: number;
  cents: number;
  dateInferred: boolean;
  accountInferred: boolean;
  fromAccount?: string;
  toAccount?: string;
  fromAccountName?: string;
  toAccountName?: string;
}
export interface FinanceCategoryTotal extends FinanceCategory { amount: number; count: number; percent: number }
export interface FinanceTrendPoint { key: string; label: string; income: number; spending: number; debtPayments: number; outflow: number; net: number }
export interface FinanceAccountBalance { id: string; name: string; type: string; balance: number; credit: boolean }
export interface FinanceDebtBalance { id: string; name: string; kind: string; balance: number }
export interface FinanceSummary { income: number; spending: number; debtPayments: number; outflow: number; net: number; transfers: number; transactionCount: number; undatedCount: number; inferredDateCount: number }
export interface FinanceAnalytics {
  month: string;
  months: { value: string; label: string }[];
  allTransactions: FinanceTransaction[];
  transactions: FinanceTransaction[];
  categories: FinanceCategoryTotal[];
  categoryOptions: FinanceCategory[];
  trend: FinanceTrendPoint[];
  summary: FinanceSummary;
  accounts: FinanceAccountBalance[];
  cashAccounts: FinanceAccountBalance[];
  availableFunds: number;
  debts: FinanceDebtBalance[];
  totalDebt: number;
  receivables: FinanceDebtBalance[];
  totalReceivable: number;
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const CATEGORY_LABELS: Record<string, string> = {
  food: "Food", transport: "Transport", bills: "Bills", home: "Home", support: "Support",
  fun: "Fun", travel: "Travel", medical: "Medical", game: "Games", subscription: "Subscriptions", other: "Other",
};
const CATEGORY_ALIASES: Record<string, string> = {
  subscriptions: "subscription", parking: "transport", fuel: "transport", toll: "transport", tolls: "transport",
  transportation: "transport", healthcare: "medical", health: "medical", medicine: "medical",
  games: "game", gaming: "game", entertainment: "fun", utilities: "bills", utility: "bills", bill: "bills",
  housing: "home", household: "home", family: "support", dining: "food", meals: "food", groceries: "food",
};
const clean = (value: unknown): string => String(value ?? "").trim();
const title = (value: string): string => value.replace(/[-_]+/g, " ").replace(/\b\w/g, (letter) => letter.toUpperCase());
const pad = (value: number): string => String(value).padStart(2, "0");
const cents = (value: unknown): number => {
  const amount = typeof value === "number" ? value : Number(value);
  return Number.isFinite(amount) ? Math.round((amount + Math.sign(amount) * Number.EPSILON) * 100) : 0;
};
const pesos = (value: number): number => value / 100;

export function normalizeFinanceCategory(value: unknown): FinanceCategory {
  const raw = clean(value).normalize("NFKC").toLowerCase().replace(/[\s_]+/g, "-") || "other";
  const key = CATEGORY_ALIASES[raw] || raw;
  return { key, label: CATEGORY_LABELS[key] || title(key) };
}

function calendarIso(year: number, month: number, day: number): string {
  if (!Number.isInteger(year) || year < 1000 || year > 9999 || month < 1 || month > 12 || day < 1 || day > 31) return "";
  const date = new Date(Date.UTC(year, month - 1, day));
  return date.getUTCFullYear() === year && date.getUTCMonth() === month - 1 && date.getUTCDate() === day
    ? `${year}-${pad(month)}-${pad(day)}` : "";
}

function parseIso(raw: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})(?:$|T)/.exec(raw);
  return match ? calendarIso(Number(match[1]), Number(match[2]), Number(match[3])) : "";
}

// No local-time Date parsing: the ledger stores Manila calendar dates. A bad
// explicit date stays undated rather than rolling Feb 30 into March.
export function resolveFinanceDate(row: FinanceSourceRow, fallbackYear: number): string {
  const authoritative = clean(row.iso) || clean(row.date_iso) || clean(row.dateIso);
  if (authoritative) return parseIso(authoritative);
  const raw = clean(row.date);
  if (/^\d{4}-/.test(raw)) return parseIso(raw);
  const human = /^([A-Za-z]+)\s+(\d{1,2})(?:,?\s+(\d{4}))?$/.exec(raw);
  const month = human ? MONTHS.findIndex((m) => m.toLowerCase() === human[1]!.slice(0, 3).toLowerCase()) + 1 : 0;
  if (human?.[3]) return calendarIso(Number(human[3]), month, Number(human[2]));
  const idDate = /^(?:[A-Za-z_-]+[:#_-])?(\d{4}-\d{2}-\d{2})(?:$|[^\d])/.exec(clean(row.id));
  if (idDate) return parseIso(idDate[1]!);
  if (!human || !month) return "";
  const monthYear = /^(\d{4})-\d{2}$/.exec(clean(row.month));
  const year = Number.isInteger(row.year) ? row.year! : monthYear ? Number(monthYear[1]) : fallbackYear;
  return calendarIso(year, month, Number(human[2]));
}

function isSchedule(row: FinanceSourceRow): boolean {
  return !!clean(row.recurs) || (row.recurring !== false && row.recurring != null && !!clean(row.recurring));
}

function hash(value: string): string {
  let result = 2166136261;
  for (let i = 0; i < value.length; i++) result = Math.imul(result ^ value.charCodeAt(i), 16777619);
  return (result >>> 0).toString(36);
}

const monthLabel = (month: string): string => `${MONTHS[Number(month.slice(5, 7)) - 1]} ${month.slice(0, 4)}`;
const validMonth = (value: string): boolean => /^\d{4}-(?:0[1-9]|1[0-2])$/.test(value);
const normalizedName = (value: string): string => value.normalize("NFKC").toLowerCase().replace(/[^\p{L}\p{N}]+/gu, " ").trim();

// API metadata is authoritative. Older responses infer the same direction from
// the owner acting as lender, or the established money-lent naming convention.
export function isReceivableDebt(debt: { name: string; lender?: string; direction?: string; receivable?: boolean }, ownerName = "Arlou"): boolean {
  if (typeof debt.receivable === "boolean") return debt.receivable;
  const owner = normalizedName(clean(ownerName));
  return /^(?:receivable|lent|outgoing-loan)$/i.test(clean(debt.direction))
    || (!!owner && normalizedName(clean(debt.lender)) === owner)
    || /^money lent to\b/i.test(clean(debt.name));
}

export function receivableBorrower(name: string): string {
  return /\b(?:lent|sale) to\s+(.+)$/i.exec(name.trim())?.[1]?.trim() || "";
}

export function isReceivablePayment(row: { debtId?: string; name?: string }, debts: readonly { id: string; name: string; lender?: string; direction?: string; receivable?: boolean }[], ownerName = "Arlou"): boolean {
  const debt = debts.find((candidate) => row.debtId ? candidate.id === row.debtId : candidate.name === row.name);
  return debt ? isReceivableDebt(debt, ownerName) : /^money lent to\b/i.test(clean(row.name));
}

function inferredDate(row: FinanceSourceRow, iso: string): boolean {
  return !!iso && !clean(row.iso) && !clean(row.date_iso) && !clean(row.dateIso)
    && !/\d{4}/.test(clean(row.date)) && !/\d{4}-\d{2}-\d{2}/.test(clean(row.id))
    && !Number.isInteger(row.year) && !/^\d{4}-\d{2}$/.test(clean(row.month));
}

export function buildFinanceAnalytics(input: FinanceAnalyticsInput, options: FinanceAnalyticsOptions = {}): FinanceAnalytics {
  const nowIso = parseIso(clean(options.nowIso)) || new Date(Date.now() + 8 * 3600 * 1000).toISOString().slice(0, 10);
  const currentMonth = nowIso.slice(0, 7);
  const year = options.fallbackYear ?? Number(nowIso.slice(0, 4));
  const month = options.month === "all" ? "all" : validMonth(options.month || "") ? options.month! : currentMonth;
  const accountLookup = new Map<string, string>();
  for (const account of input.accounts || []) accountLookup.set(account.id, account.name);
  for (const debt of input.debts || []) {
    if (!accountLookup.has(debt.id)) accountLookup.set(debt.id, debt.name);
    if (debt.facility && !accountLookup.has(debt.facility)) accountLookup.set(debt.facility, debt.lender || title(debt.facility));
  }
  const accountName = (id: string): string => id ? accountLookup.get(id) || title(id) : "Unrecorded account";
  const transferAccountName = (id: string): string => id ? accountName(id) : "External / unrecorded account";
  const allTransactions: FinanceTransaction[] = [];
  const seenIds = new Set<string>();
  const generatedIds = new Map<string, number>();
  const add = (kind: FinanceTransactionKind, rows: readonly FinanceSourceRow[] = []) => {
    for (const row of rows) {
      if (isSchedule(row)) continue;
      const iso = resolveFinanceDate(row, year);
      const amountCents = cents(row.amount);
      let account = clean(row.account);
      let accountInferred = false;
      // Some reconciled legacy card repayments omit their funding account,
      // while an exact matching transfer recorded it. Enrich only an unambiguous
      // match; keep both records visible without adding transfers to spending.
      if (kind === "debt-payment" && !account && row.debtId && iso) {
        const debt = input.debts?.find((candidate) => candidate.id === row.debtId);
        const targets = new Set([row.debtId, debt?.facility].filter(Boolean));
        const matches = allTransactions.filter((candidate) => candidate.kind === "transfer"
          && candidate.iso === iso && candidate.cents === amountCents && targets.has(candidate.toAccount)
          && candidate.fromAccount);
        if (matches.length === 1) { account = matches[0]!.fromAccount!; accountInferred = true; }
      }
      const category = kind === "expense" ? normalizeFinanceCategory(row.cat || row.category) : { key: "", label: "" };
      const from = clean(row.from);
      const to = clean(row.to);
      const label = clean(row.note) || clean(row.source) || clean(row.name) || (kind === "transfer" ? `${transferAccountName(from)} to ${transferAccountName(to)}` : title(kind));
      const suppliedId = clean(row.id);
      const fingerprint = hash(JSON.stringify([kind, iso, row.date, label, category.key, account, from, to, amountCents, row.debtId]));
      const occurrence = generatedIds.get(fingerprint) || 0;
      generatedIds.set(fingerprint, occurrence + 1);
      const id = `${kind}:${suppliedId || `${fingerprint}:${occurrence}`}`;
      // Repeated IDs are a duplicate response row. Two identical payments with
      // different IDs remain separate actual payments, even on the same day.
      if (suppliedId && seenIds.has(id)) continue;
      seenIds.add(id);
      allTransactions.push({
        id, kind, iso, date: iso || clean(row.date) || "Undated", label,
        category: category.key, categoryLabel: category.label, account: kind === "transfer" ? from || to : account,
        accountName: kind === "transfer" ? `${transferAccountName(from)} → ${transferAccountName(to)}` : accountName(account),
        amount: pesos(amountCents), cents: amountCents, dateInferred: inferredDate(row, iso), accountInferred,
        ...(kind === "transfer" ? { fromAccount: from, toAccount: to, fromAccountName: transferAccountName(from), toAccountName: transferAccountName(to) } : {}),
      });
    }
  };
  add("income", input.incomes);
  add("expense", input.expenses);
  add("transfer", input.transfers);
  // Collections already have actual income rows. A legacy response may also
  // expose the raw debt payment; exclude that view rather than counting twice.
  add("debt-payment", input.payments?.filter((row) => !isReceivablePayment(row, input.debts || [], options.ownerName)));
  allTransactions.sort((a, b) => b.iso.localeCompare(a.iso) || a.id.localeCompare(b.id));

  const monthSet = new Set(allTransactions.filter((row) => row.iso).map((row) => row.iso.slice(0, 7)));
  monthSet.add(currentMonth);
  if (month !== "all") monthSet.add(month);
  const months = [...monthSet].sort().reverse().map((value) => ({ value, label: monthLabel(value) }));
  const accountFilter = clean(options.account);
  const categoryFilter = clean(options.category) ? normalizeFinanceCategory(options.category).key : "";
  const transactions = allTransactions.filter((row) =>
    (month === "all" || row.iso.slice(0, 7) === month)
    && (!accountFilter || row.account === accountFilter || row.fromAccount === accountFilter || row.toAccount === accountFilter)
    && (!categoryFilter || (row.kind === "expense" && row.category === categoryFilter)),
  );
  const categoryOptions = [...new Map(allTransactions.filter((row) => row.kind === "expense").map((row) => [row.category, { key: row.category, label: row.categoryLabel }])).values()]
    .sort((a, b) => a.label.localeCompare(b.label));
  let income = 0, spending = 0, debtPayments = 0, transfers = 0;
  const categoryTotals = new Map<string, { label: string; cents: number; count: number }>();
  for (const row of transactions) {
    if (row.kind === "income") income += row.cents;
    if (row.kind === "expense") {
      spending += row.cents;
      const previous = categoryTotals.get(row.category) || { label: row.categoryLabel, cents: 0, count: 0 };
      categoryTotals.set(row.category, { label: previous.label, cents: previous.cents + row.cents, count: previous.count + 1 });
    }
    if (row.kind === "debt-payment") debtPayments += row.cents;
    if (row.kind === "transfer") transfers += row.cents;
  }
  const categories = [...categoryTotals].map(([key, value]) => ({ key, label: value.label, amount: pesos(value.cents), count: value.count, percent: spending > 0 ? value.cents / spending * 100 : 0 }))
    .sort((a, b) => b.amount - a.amount || a.label.localeCompare(b.label));
  const trendTotals = new Map<string, { income: number; spending: number; debtPayments: number }>();
  if (month !== "all") {
    const days = new Date(Date.UTC(Number(month.slice(0, 4)), Number(month.slice(5, 7)), 0)).getUTCDate();
    for (let day = 1; day <= days; day++) trendTotals.set(`${month}-${pad(day)}`, { income: 0, spending: 0, debtPayments: 0 });
  }
  for (const row of transactions) {
    if (!row.iso || row.kind === "transfer") continue;
    const key = month === "all" ? row.iso.slice(0, 7) : row.iso;
    const point = trendTotals.get(key) || { income: 0, spending: 0, debtPayments: 0 };
    if (row.kind === "income") point.income += row.cents;
    if (row.kind === "expense") point.spending += row.cents;
    if (row.kind === "debt-payment") point.debtPayments += row.cents;
    trendTotals.set(key, point);
  }
  const trend = [...trendTotals].sort(([a], [b]) => a.localeCompare(b)).map(([key, value]) => ({
    key, label: month === "all" ? monthLabel(key) : String(Number(key.slice(8, 10))),
    income: pesos(value.income), spending: pesos(value.spending), debtPayments: pesos(value.debtPayments),
    outflow: pesos(value.spending + value.debtPayments), net: pesos(value.income - value.spending - value.debtPayments),
  }));
  const accounts = (input.accounts || []).map((account) => ({ ...account, balance: pesos(cents(account.balance)), credit: isCreditAccount(account) }));
  const cashAccounts = accounts.filter((account) => !account.credit).sort((a, b) => b.balance - a.balance);
  const debtBalances = (input.debts || []).map((debt) => ({ id: debt.id, name: debt.name, kind: debt.kind || "other", balance: pesos(cents(debt.balance)), receivable: isReceivableDebt(debt, options.ownerName) }))
    .filter((debt) => debt.balance > 0).sort((a, b) => b.balance - a.balance);
  const debts = debtBalances.filter((debt) => !debt.receivable).map(({ receivable: _receivable, ...debt }) => debt);
  const receivables = debtBalances.filter((debt) => debt.receivable).map(({ receivable: _receivable, ...debt }) => debt);
  return {
    month, months, allTransactions, transactions, categories, categoryOptions, trend,
    summary: { income: pesos(income), spending: pesos(spending), debtPayments: pesos(debtPayments), outflow: pesos(spending + debtPayments), net: pesos(income - spending - debtPayments), transfers: pesos(transfers), transactionCount: transactions.length, undatedCount: transactions.filter((row) => !row.iso).length, inferredDateCount: transactions.filter((row) => row.dateInferred).length },
    accounts, cashAccounts, availableFunds: availableCash(accounts),
    debts, totalDebt: pesos(debts.reduce((total, debt) => total + cents(debt.balance), 0)),
    receivables, totalReceivable: pesos(receivables.reduce((total, debt) => total + cents(debt.balance), 0)),
  };
}
